import { type UserPrefsType } from '@/types';

const NAMESPACE = 'devo_studio';

const KEY: Record<string, string> = {
  CONVERSATION_ID: 'conversationId',
  CHAT_SESSION_ID: 'chatSessionId', // Legacy key for backward compatibility
  TOS_ACCEPTED: 'tosAccepted',
  THEME: 'theme',
  USER_METADATA: 'userMetadata',
  USER_PREFS: 'userPrefs',
};

export const UserPrefsKey = {
  FAVORITE_PROMPTS: 'favPrompts',
  PERSONA_TAB: 'personaTab',
  ADMIN_PERSONAS_TAB: 'adminPersonasTab',
};

// Legacy constants for backward compatibility
const LAST_USER_ID = '$last';

const getNamespacedKey = (userId?: string | null, key?: string | null) =>
  [NAMESPACE, userId ?? '?', key ?? '?'].join('.');

// Loads the last value stored for a given userId & key in either
// localStorage or sessionStorage. Assumes the value is stored as a JSON string.
const getStoredUserData = ({
  userId,
  key,
  disk,
}: {
  userId?: string | null;
  key?: string | null;
  disk?: boolean;
}): unknown => {
  if (!userId || !key) {
    return null;
  }
  const namespacedKey = getNamespacedKey(userId, key);
  const storage = disk ? localStorage : sessionStorage;
  try {
    const storedValue = storage.getItem(namespacedKey) ?? 'null';
    return JSON.parse(storedValue);
  } catch (error) {
    console.error(`Error parsing stored value for key ${namespacedKey}:`, error);
    return null;
  }
};

// Stores (or clears) the value stored for a given userId & key in either
// localStorage or sessionStorage.
const setStoredUserData = ({
  userId,
  key,
  value,
  disk,
}: {
  userId?: string | null;
  key?: string | null;
  value?: string | number | boolean | null | object;
  disk?: boolean;
}) => {
  if (!userId || !key) {
    return null;
  }
  const namespacedKey = getNamespacedKey(userId, key);
  const storage = disk ? localStorage : sessionStorage;
  try {
    if (value !== null && value !== undefined) {
      storage.setItem(namespacedKey, JSON.stringify(value));
    } else {
      storage.removeItem(namespacedKey);
    }
  } catch (error) {
    console.error(`Error storing value for key ${namespacedKey}:`, error);
  }
};

// Loads the last stored timestamp that a given user accepted the Terms of Service.
export const getStoredTosAccepted = (userId?: string | null) =>
  getStoredUserData({
    userId,
    key: KEY.TOS_ACCEPTED,
    disk: true,
  });

// Stores (or removes) the given timestamp that a given user accepted the Terms of Service.
export const setStoredTosAccepted = (userId?: string | null, tosAccepted?: number | null) =>
  setStoredUserData({
    userId,
    key: KEY.TOS_ACCEPTED,
    value: tosAccepted,
    disk: true,
  });

export const getStoredTheme = (userId?: string | null) =>
  String(getStoredUserData({ userId, key: KEY.THEME, disk: true }) ?? '');

export const setStoredTheme = (userId?: string | null, theme?: string | null) =>
  setStoredUserData({ userId, key: KEY.THEME, value: theme, disk: true });

export const getLastStoredUserMetadata = () =>
  getStoredUserData({ userId: LAST_USER_ID, key: KEY.USER_METADATA, disk: true }) || null;

export const setLastStoredUserMetadata = (metadata?: object | null) =>
  setStoredUserData({ userId: LAST_USER_ID, key: KEY.USER_METADATA, value: metadata, disk: true });

export const getStoredUserPrefs = (userId?: string | null) =>
  getStoredUserData({ userId, key: KEY.USER_PREFS, disk: true }) || null;

export const setStoredUserPrefs = (userId?: string | null, userPrefs?: UserPrefsType | null) =>
  setStoredUserData({ userId, key: KEY.USER_PREFS, value: userPrefs, disk: true });

// Backward compatible conversation ID functions
export const getStoredConversationId = (userId?: string | null) => {
  // First try the new key
  let stored = getStoredUserData({
    userId,
    key: KEY.CONVERSATION_ID,
    disk: false,
  });

  // If not found, try the legacy key and migrate it
  if (!stored) {
    stored = getStoredUserData({
      userId,
      key: KEY.CHAT_SESSION_ID,
      disk: false,
    });

    // If found in legacy location, migrate it
    if (stored) {
      setStoredConversationId(userId, stored as string);
      // Remove the old key
      setStoredUserData({
        userId,
        key: KEY.CHAT_SESSION_ID,
        value: null,
        disk: false,
      });
    }
  }

  return stored;
};

export const setStoredConversationId = (userId?: string | null, conversationId?: string | null) =>
  setStoredUserData({
    userId,
    key: KEY.CONVERSATION_ID,
    value: conversationId,
    disk: false,
  });
